import React, { useState } from "react";
import { ArrowLeft, User, Shield, Info, Layers, ImageIcon } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactFlow, { Background, Controls } from "reactflow";
import "reactflow/dist/style.css";
import ReactMarkdown from "react-markdown";


type GuideStep = {
  id: string;
  title: string;
  description: string;
  warning?: string;
  imagePath?: string;
};

// Exact steps matching the user's attached Admin Onboarding Guide image
const ADMIN_STEPS: GuideStep[] = [
  {
    id: "step-admin-0",
    title: "Create Tenant Account",
    description: "A tenant name is required to use the app. To create a tenant account send a mail to support@nesterra.net with the desired tenant name. You will receive an email with further instructions."
  },
  {
    id: "step-admin-1",
    title: "Start Setup",
    description: "Enter the finalized tenant name while enabling the app for your organization. A tenant name can only have lowercase alphabets, numbers, and hyphens.",
    warning: "Make sure you are using the approved tenant name.",
    imagePath: "/assets/docs/okta-oin/tenant-label.png"
  },
  {
    id: "step-admin-2",
    title: "Exchange Client Credentials",
    description: "Once the app is added share the Client Id, Client Secret and Okta Domain with the app support team.",
    imagePath: "/assets/docs/okta-oin/client-credentials.png"
  },
  {
    id: "step-admin-3",
    title: "Confirmation",
    description: "Once the configuration is ready you will receive a confirmation email."
  }
];

// User Onboarding Guide steps matching the attached images
const USER_BASE_STEPS: GuideStep[] = [
  {
    id: "step-user-0",
    title: "Get Started",
    description: "The application supports IdP and SP initiated flow. The application can be launched from Okta OIN catalog (IdP Flow), or accessed directly using the URL (SP Flow) provided by your organization. The URL will be in the format of https://platform.nesterra.net/auth/login"
  },
  {
    id: "step-user-1",
    title: "Sign In",
    description: "Sign in using the Okta credentials using your Organizations Okta intance.",
  },
  {
    id: "step-user-2",
    title: "Check Request Status",
    description: "You can check the status of your request in using url https://platform.nesterra.net/t/<tenant-name>/waiting-room.",
    warning: "It takes 1-2 business days to process your request.",
  }
];

const USER_GRANTED_END_STEPS: GuideStep[] = [
  {
    id: "step-user-3-granted",
    title: "Access Granted",
    description: "Once your request is approved, you will receive an email (if email is configured for your tenant)",
    warning: "It takes 1-2 business days to process your request.",
  }
];

const USER_DENIED_END_STEPS: GuideStep[] = [
  {
    id: "step-user-4-denied",
    title: "Access Rejected",
    description: "If your request is denied, you will receive an email notification with the reason for denial.",
  },
  {
    id: "step-user-5-denied",
    title: "Contact Support",
    description: "Reach out to our support team for clarification on why your request was denied and what steps you can take."
  },
  {
    id: "step-user-6-denied",
    title: "Resubmit Request",
    description: "After addressing the issues, you can contact support team with your details - Name, Email, Organization, Url, Justification.",
    warning: "Make sure to provide valid information."
  }
];

const CONFIG_GUIDE_MARKDOWN = `## Prerequisites

Before configuring SAML SSO for SmartSite in Okta, ensure you have:
1. An active Okta administrator account.
2. A confirmed tenant name for SmartSite. If you do not have one, please contact the support team at [support@nesterra.net](mailto:support@nesterra.net) to create your tenant.

## Supported features

* SP-initiated SSO (Single Sign-On)
* IdP-initiated SSO
* Just-In-Time (JIT) provisioning

## Configuration steps

1. In the Okta Admin Console, add the **SmartSite** application. Follow the path, in Okta Go to Applications → Browse App Catalog, and search and add app. 
2. Go to the App → Sign On tab and note of the Client ID and Client Secret.
3. Contact the SmartSite team at [support@nesterra.net](mailto:support@nesterra.net) and provide them with your **Tenant Name**, **Client Id**, **Client Secret** and **Okta Domain**.
4. The SmartSite team will enable configure the SSO & confirm the setup. You will receive an email with the details.

## SP-initiated SSO

1. Navigate to the SmartSite login page at [https://platform.nesterra.net/auth/login](https://platform.nesterra.net/auth/login).
2. Enter your Email Address. The system will detect your tenant's SSO configuration and provide you to option to login to your tenant.
3. Upon successfull authentication you will be redirected to the SmartSite portal, with the list of applications you have access to.`;

// Custom Step Image Component that falls back to a clean placeholder showing the required filename
const StepImage: React.FC<{ src: string }> = ({ src }) => {
  const [hasError, setHasError] = useState(false);
  const filename = src.split("/").pop() || "";

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl bg-slate-50 dark:bg-slate-950 p-4 text-center">
        <ImageIcon className="w-8 h-8 text-slate-400 mb-2" />
        <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
          Image Placeholder
        </span>
        <span className="text-[10px] font-mono text-slate-400 dark:text-slate-500 mt-1 select-all">
          {filename}
        </span>
      </div>
    );
  }

  return (
    <div className="relative border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden max-h-48 flex items-center justify-center bg-slate-50 dark:bg-slate-950">
      <img
        src={src}
        alt={filename}
        onError={() => setHasError(true)}
        className="w-full h-full object-contain max-h-40 p-2"
      />
    </div>
  );
};

export const OktaOinGuidePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get("role");
  const [activePersona, setActivePersona] = useState<"admin" | "user">(
    roleParam === "user" ? "user" : "admin"
  );
  const [userFlowType, setUserFlowType] = useState<"granted" | "denied">("granted");
  const [copied, setCopied] = useState(false);
  const [showRawMarkdown, setShowRawMarkdown] = useState(false);

  const handleCopyMarkdown = () => {
    navigator.clipboard.writeText(CONFIG_GUIDE_MARKDOWN);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const steps = activePersona === "admin"
    ? ADMIN_STEPS
    : [...USER_BASE_STEPS, ...(userFlowType === "granted" ? USER_GRANTED_END_STEPS : USER_DENIED_END_STEPS)];

  // Node base styles matching the clean simple boxes in the reference image
  const baseNodeStyle = {
    background: "#fff",
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    padding: "10px 14px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#0f172a",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
    width: 170,
    textAlign: "center" as const
  };

  // React Flow definitions representing Tenant Onboarding (Admin) vertically stacked
  const adminNodes = [
    { id: "step-admin-0", data: { label: "1. Create Tenant Account" }, position: { x: 120, y: 20 }, style: baseNodeStyle },
    { id: "step-admin-1", data: { label: "2. Start Setup" }, position: { x: 120, y: 110 }, style: baseNodeStyle },
    { id: "step-admin-2", data: { label: "3. Exchange Credentials" }, position: { x: 120, y: 200 }, style: baseNodeStyle },
    { id: "step-admin-3", data: { label: "4. Confirmation" }, position: { x: 120, y: 290 }, style: baseNodeStyle }
  ];

  const adminEdges = [
    { id: "e-admin-0-1", source: "step-admin-0", target: "step-admin-1", animated: false, style: { stroke: "#f97316", strokeWidth: 2 } },
    { id: "e-admin-1-2", source: "step-admin-1", target: "step-admin-2", animated: false, style: { stroke: "#f97316", strokeWidth: 2 } },
    { id: "e-admin-2-3", source: "step-admin-2", target: "step-admin-3", animated: false, style: { stroke: "#f97316", strokeWidth: 2 } }
  ];

  // User flow nodes stacked vertically with branching paths
  const userNodes = [
    { id: "step-user-0", data: { label: "1. Get Started" }, position: { x: 140, y: 20 }, style: baseNodeStyle },
    { id: "step-user-1", data: { label: "2. Request Access" }, position: { x: 140, y: 110 }, style: baseNodeStyle },
    { id: "step-user-2", data: { label: "3. Check Status" }, position: { x: 140, y: 200 }, style: baseNodeStyle },
    // Branch 1: Access Granted (curves out left/down)
    {
      id: "step-user-3-granted",
      data: { label: "4. Access Granted" },
      position: { x: 20, y: 290 },
      style: {
        ...baseNodeStyle,
        background: userFlowType === "granted" ? "#f0fdf4" : "#fff",
        border: userFlowType === "granted" ? "2px solid #22c55e" : "1px solid #cbd5e1"
      }
    },
    // Branch 2: Access Denied Flow (curves out right/down)
    {
      id: "step-user-4-denied",
      data: { label: "4. Access Rejected" },
      position: { x: 260, y: 390 },
      style: {
        ...baseNodeStyle,
        background: userFlowType === "denied" ? "#fef2f2" : "#fff",
        border: userFlowType === "denied" ? "2px solid #ef4444" : "1px solid #cbd5e1"
      }
    },
    { id: "step-user-5-denied", data: { label: "5. Contact Support" }, position: { x: 260, y: 480 }, style: baseNodeStyle },
    { id: "step-user-6-denied", data: { label: "6. Resubmit Request" }, position: { x: 260, y: 570 }, style: baseNodeStyle }
  ];

  const userEdges = [
    { id: "e-user-0-1", source: "step-user-0", target: "step-user-1", animated: false, style: { stroke: "#cbd5e1", strokeWidth: 2 } },
    { id: "e-user-1-2", source: "step-user-1", target: "step-user-2", animated: false, style: { stroke: "#cbd5e1", strokeWidth: 2 } },
    // Branch forks from step-user-2 (Check Status)
    { id: "e-user-2-3g", source: "step-user-2", target: "step-user-3-granted", animated: false, style: { stroke: "#22c55e", strokeWidth: 2 } },
    { id: "e-user-2-4d", source: "step-user-2", target: "step-user-4-denied", animated: false, style: { stroke: "#ef4444", strokeWidth: 2 } },
    // Denied sub-steps edges
    { id: "e-user-4d-5d", source: "step-user-4-denied", target: "step-user-5-denied", animated: false, style: { stroke: "#ef4444", strokeWidth: 2 } },
    { id: "e-user-5d-6d", source: "step-user-5-denied", target: "step-user-6-denied", animated: false, style: { stroke: "#ef4444", strokeWidth: 2 } },
    // LOOP Connection: Resubmit Request (8) loops back up to Check Status (3)
    { id: "e-user-6d-2", source: "step-user-6-denied", target: "step-user-2", animated: false, style: { stroke: "#ef4444", strokeWidth: 2, strokeDasharray: "5,5" } }
  ];

  const flowNodes = activePersona === "admin" ? adminNodes : userNodes;
  const flowEdges = activePersona === "admin" ? adminEdges : userEdges;

  const handleNodeClick = (_event: React.MouseEvent, node: any) => {
    if (node.id.includes("granted")) {
      setUserFlowType("granted");
    } else if (node.id.includes("denied")) {
      setUserFlowType("denied");
    }

    const targetElement = document.getElementById(node.id);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
      targetElement.classList.add("ring-2", "ring-orange-500");
      setTimeout(() => {
        targetElement.classList.remove("ring-2", "ring-orange-500");
      }, 2000);
    }
  };

  return (
    <div className="w-full min-h-screen bg-slate-50 dark:bg-slate-950 p-6 lg:p-12 selection:bg-orange-500 selection:text-white">
      {/* Attribution hiding styles */}
      <style>{`.react-flow__attribution { display: none !important; }`}</style>
      <div className="mx-auto max-w-4xl">

        {/* Back Link */}
        <button
          onClick={() => navigate("/docs/integrations")}
          className="flex items-center text-xs font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Integrations
        </button>

        {/* Title */}
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-500">
            Okta Integration Network (OIN)
          </p>
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
            Okta Integration Network (OIN) Guide
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            Configure SSO, manage user onboarding, and access submission templates for Okta.
          </p>
        </div>

        {/* OIN Configuration Guide Markdown Section */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 mb-10 shadow-sm text-slate-800 dark:text-slate-200 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl -z-10" />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-lg font-bold text-slate-950 dark:text-white flex items-center gap-2">
                <Info className="w-5 h-5 text-orange-500" />
                Okta Customer Configuration Guide
              </h2>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowRawMarkdown(!showRawMarkdown)}
                className="px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-950 hover:bg-slate-200 dark:hover:bg-slate-900 text-slate-700 dark:text-slate-300"
              >
                {showRawMarkdown ? "Show Preview" : "Show Raw Markdown"}
              </button>
              <button
                onClick={handleCopyMarkdown}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${copied
                  ? "bg-green-600 text-white"
                  : "bg-orange-500 hover:bg-orange-600 text-white"
                  }`}
              >
                {copied ? "Copied!" : "Copy Markdown"}
              </button>
            </div>
          </div>

          <div className="bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-900 p-5 max-h-[350px] overflow-y-auto font-sans leading-relaxed text-sm select-text">
            {showRawMarkdown ? (
              <pre className="font-mono text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap selection:bg-orange-500/30">
                {CONFIG_GUIDE_MARKDOWN}
              </pre>
            ) : (
              <div className="text-slate-700 dark:text-slate-300 select-text">
                <ReactMarkdown
                  components={{
                    h2: ({ node, ...props }) => <h2 className="text-base font-bold text-slate-900 dark:text-white border-b border-slate-250 dark:border-slate-800 pb-2 mb-3 mt-6 first:mt-0" {...props} />,
                    h3: ({ node, ...props }) => <h3 className="text-sm font-semibold text-slate-900 dark:text-white mt-4 mb-2" {...props} />,
                    p: ({ node, ...props }) => <p className="text-slate-700 dark:text-slate-300 mb-3" {...props} />,
                    ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-3 space-y-1 text-slate-700 dark:text-slate-300" {...props} />,
                    ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-3 space-y-1 text-slate-700 dark:text-slate-300" {...props} />,
                    li: ({ node, ...props }) => <li className="text-slate-700 dark:text-slate-300" {...props} />,
                    a: ({ node, ...props }) => <a className="text-orange-600 dark:text-orange-400 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
                    code: ({ node, ...props }) => <code className="bg-slate-200/50 dark:bg-slate-900 px-1 py-0.5 rounded text-orange-700 dark:text-orange-400 font-mono text-xs" {...props} />,
                    strong: ({ node, ...props }) => <strong className="text-slate-900 dark:text-white font-bold" {...props} />,
                  }}
                >
                  {CONFIG_GUIDE_MARKDOWN}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>

        {/* Persona Switcher Tabs */}
        <div className="mb-8 border-b border-slate-200 dark:border-slate-800 flex gap-6">
          <button
            onClick={() => setActivePersona("admin")}
            className={`pb-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-all ${activePersona === "admin"
              ? "border-orange-500 text-orange-500"
              : "border-transparent text-slate-500 hover:text-slate-850 dark:hover:text-slate-200"
              }`}
          >
            <Shield className="w-4 h-4" />
            Admin Workflow
          </button>
          <button
            onClick={() => setActivePersona("user")}
            className={`pb-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-all ${activePersona === "user"
              ? "border-orange-500 text-orange-500"
              : "border-transparent text-slate-500 hover:text-slate-850 dark:hover:text-slate-200"
              }`}
          >
            <User className="w-4 h-4" />
            User Workflow
          </button>
        </div>

        {/* Flow Switcher for User Persona (Granted / Denied) */}
        {activePersona === "user" && (
          <div className="mb-8 flex gap-3">
            <button
              onClick={() => setUserFlowType("granted")}
              className={`px-4 py-2 rounded-2xl text-xs font-bold transition-all border ${userFlowType === "granted"
                ? "bg-green-600 border-green-600 text-white shadow-sm"
                : "bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400"
                }`}
            >
              View Access Granted Flow
            </button>
            <button
              onClick={() => setUserFlowType("denied")}
              className={`px-4 py-2 rounded-2xl text-xs font-bold transition-all border ${userFlowType === "denied"
                ? "bg-red-600 border-red-600 text-white shadow-sm"
                : "bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400"
                }`}
            >
              View Access Denied Flow
            </button>
          </div>
        )}

        {/* Workflow Diagram Panel (React Flow) - Taller Card with Vertical Orientation */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 mb-10 shadow-sm">
          <h2 className="text-base font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
            <Layers className="w-4.5 h-4.5 text-orange-500" />
            {activePersona === "admin" ? "Tenant Onboarding Workflow" : "User Onboarding Workflow"}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">
            Click on any flow node below to jump directly to the detailed configuration instructions.
          </p>

          <div className={`w-full ${activePersona === "admin" ? "h-[400px]" : "h-[680px]"} bg-slate-50 dark:bg-slate-950 border border-slate-200/60 dark:border-slate-800 rounded-2xl overflow-hidden relative`}>
            <ReactFlow
              nodes={flowNodes}
              edges={flowEdges}
              onNodeClick={handleNodeClick}
              nodesDraggable={false}
              nodesConnectable={false}
              zoomOnScroll={false}
              panOnDrag={true}
              zoomOnPinch={false}
              zoomOnDoubleClick={false}
              proOptions={{ hideAttribution: true }}
              fitView
              fitViewOptions={{ padding: 0.15 }}
            >
              <Background gap={12} size={1} />
              <Controls showInteractive={false} />
            </ReactFlow>
          </div>
        </div>

        {/* Detailed Configuration Steps */}
        <div className="space-y-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${activePersona}-${userFlowType}`}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.2 }}
              className="space-y-8"
            >
              {steps.map((step, idx) => {
                const stepNum = idx + 1;
                return (
                  <div
                    key={step.id}
                    id={step.id}
                    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-sm flex flex-col md:flex-row gap-6 items-start transition-all"
                  >
                    {/* Step Description */}
                    <div className="flex-1 space-y-4">
                      <div>
                        {/* Beautiful Pill Label for Step Numbers */}
                        <div className="inline-flex items-center">
                          <span className="bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 text-xs font-bold px-3 py-1.5 rounded-full border border-blue-100 dark:border-blue-900/50 shadow-sm">
                            Step {stepNum}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-950 dark:text-white mt-4">
                          {step.title}
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">
                          {step.description}
                        </p>
                      </div>

                      {step.warning && (
                        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-900/30 rounded-2xl p-4 flex gap-3 text-xs text-amber-700 dark:text-amber-400">
                          <Info className="w-4.5 h-4.5 text-amber-500 shrink-0 mt-0.5" />
                          <p>{step.warning}</p>
                        </div>
                      )}
                    </div>

                    {/* Screenshot Static Target Placeholder */}
                    {step.imagePath && (
                      <div className="w-full md:w-80 shrink-0 space-y-2">
                        <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">
                          Step Illustration
                        </span>
                        <StepImage src={step.imagePath} />
                      </div>
                    )}
                  </div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Need Help footer matching Admin Onboarding Guide image */}
        <div className="mt-8 bg-blue-50/50 dark:bg-slate-900/40 border border-blue-100 dark:border-slate-850 rounded-3xl p-6">
          <h3 className="text-base font-bold text-blue-900 dark:text-blue-400 mb-2">Need Help?</h3>
          <a
            href="mailto:support@nesterra.net"
            className="text-sm font-semibold text-blue-700 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
          >
            support@nesterra.net
          </a>
        </div>

      </div>
    </div>
  );
};

export default OktaOinGuidePage;
